/**
 * Pure shared logic for the offline write-outbox.
 * No DOM, no IndexedDB, no side-effects — safe to import in both the app
 * bundle and inside the service worker.
 */

/** 24-hour TTL for outbox entries (server dedup window mirrors this). */
export const OUTBOX_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Returns true when the outbox entry was enqueued more than OUTBOX_TTL_MS ago.
 *
 * @param {{ enqueuedAt: number }} entry  — entry with ms-since-epoch timestamp
 * @param {number} now  — ms-since-epoch (injectable for tests)
 * @returns {boolean}
 */
export function isExpired(entry, now) {
  return now - entry.enqueuedAt >= OUTBOX_TTL_MS;
}

/**
 * Generate a new stable client-side key that doubles as both the outbox
 * entry id and the Idempotency-Key request header value.
 *
 * `crypto.randomUUID()` is only defined in secure contexts (HTTPS or
 * localhost); on plain HTTP — e.g. testing over LAN from a phone — it is
 * undefined, so every outbox-eligible request would throw before `fetch` is
 * even called. `crypto.getRandomValues()` has no such restriction, so build a
 * v4 UUID from it instead. The key only needs to be collision-resistant
 * (it is an idempotency key, not a security token), so `Math.random()` is an
 * acceptable last-resort fallback if `crypto` itself is unavailable.
 *
 * @returns {string}
 */
export function newOutboxKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Decide whether a request should be queued in the outbox on network failure.
 *
 * True only when ALL of:
 *   - method is not GET (i.e. it is a mutation)
 *   - Content-Type starts with application/json (not multipart, not form)
 *   - the X-Outbox: queue marker header is present (explicit opt-in)
 *
 * @param {{ method: string, contentType: string, hasOutboxHeader: boolean }} opts
 * @returns {boolean}
 */
export function shouldQueue({ method, contentType, hasOutboxHeader }) {
  if (method.toUpperCase() === 'GET') return false;
  if (!contentType || !contentType.startsWith('application/json')) return false;
  if (!hasOutboxHeader) return false;
  return true;
}
